'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useState } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { ArrowRight, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { HOTEL_EMAIL } from '@/lib/contact';
import { useFaqCount } from '@/lib/faq-hooks';

function FloatingBookNow() {
  const t = useTranslations('rooms');
  const visible = true;

  return (
    <>
      {/* Desktop: floating bottom-right button */}
      <div
        className={`fixed right-8 z-40 hidden md:block transition-all duration-500 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{ bottom: 'calc(2rem + var(--selection-bar-height))' }}
      >
        <Link
          href="/rooms#rooms-list"
          className="group inline-flex items-center gap-2 px-[26px] py-4 bg-[#801515] hover:bg-[#601010] text-white font-serif text-lg rounded-full shadow-2xl hover:shadow-[0_8px_30px_rgba(128,21,21,0.45)] transition-all duration-300 hover:scale-105"
        >
          {t('bookNow')}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Mobile: sticky bottom bar */}
      <div
        className={`fixed left-0 right-0 md:hidden z-50 transition-all duration-500 ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', bottom: 'var(--selection-bar-height)' }}
      >
        <Link
          href="/rooms#rooms-list"
          className="flex items-center justify-center gap-2 w-full py-4 bg-[#801515] text-white text-base font-bold uppercase tracking-wider border-t border-black/20"
          style={{ boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.15)' }}
        >
          {t('bookNow')}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </>
  );
}

export default function ContactPage() {
  const t = useTranslations('design.contact');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const faqCount = useFaqCount('design.contact.faq');

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />
      <FloatingBookNow />

      {/* Hero Section - Full Width with Castle Entrance */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/uploads/1777545906487-d_11.jpg"
            alt="Castle entrance with ornate ironwork"
            fill
            className="object-cover image-breathe-slow"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/25" />

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-48 lg:pt-0 text-center">
          <div className="observe-me slide-up w-full max-w-3xl px-12 py-14 md:px-20 md:py-16">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>{t('hero.subtitle')}</p>
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>
            <p className="text-xl sm:text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1px_1px_3px] sm:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Section - Grid Layout */}
      <section className="design-section-gap rounded-3xl overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Daniel Castle Resort Section */}
          <div className="design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6] observe-me slide-up">
            <h2 className="font-poppins text-4xl md:text-5xl font-bold mb-8 text-[#801515]">
              {t('danielCastleResort.heading')}
            </h2>

            {/* Email */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[#801515] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href={`mailto:${HOTEL_EMAIL}`} className="text-lg text-gray-700 hover:text-[#801515] transition-colors font-semibold">
                  {HOTEL_EMAIL}
                </a>
              </div>
            </div>

            {/* Reception Section */}
            <div className="pt-8 border-t border-gray-300">
              <h3 className="font-poppins text-2xl font-semibold text-gray-900 mb-4">
                {t('reception.heading')}
              </h3>
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-[#801515] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+40725942787" className="text-lg text-gray-700 hover:text-[#801515] transition-colors font-semibold">
                    +40 725 942 787
                  </a>
                </div>
                <p className="text-sm text-gray-600 ml-8">
                  {t('reception.hours')}
                </p>
              </div>
            </div>

            {/* Corporate Section */}
            <div className="pt-8 border-t border-gray-300">
              <h3 className="font-poppins text-2xl font-semibold text-gray-900 mb-4">
                {t('corporate.heading')}
              </h3>
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-[#801515] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+40741129484" className="text-lg text-gray-700 hover:text-[#801515] transition-colors font-semibold">
                    +40 741 129 484
                  </a>
                </div>
                <p className="text-sm text-gray-600 ml-8">
                  {t('corporate.hours')}
                </p>
              </div>
            </div>

            {/* Events Section */}
            <div className="pt-8 border-t border-gray-300">
              <h3 className="font-poppins text-2xl font-semibold text-gray-900 mb-4">
                {t('events.heading')}
              </h3>
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-5 h-5 text-[#801515] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+40721934446" className="text-lg text-gray-700 hover:text-[#801515] transition-colors font-semibold">
                    +40 721 934 446
                  </a>
                </div>
                <p className="text-sm text-gray-600 ml-8">
                  {t('events.hours')}
                </p>
              </div>
            </div>
          </div>

          {/* Address & Directions Section */}
          <div className="design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f5f3f0] to-[#faf8f6] observe-me slide-up" style={{ animationDelay: '0.2s' }}>
            <h2 className="font-poppins text-4xl md:text-5xl font-bold mb-8 text-[#801515]">
              {t('address.heading')}
            </h2>

            {/* Address Details */}
            <div className="mb-8">
              <div className="flex gap-3 mb-4">
                <svg className="w-6 h-6 text-[#801515] flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    {t('address.location')}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {t('address.street')}<br />
                    {t('address.county')}<br />
                    {t('address.country')}
                  </p>
                </div>
              </div>

              {/* Coordinates */}
              <div className="mt-6 p-4 bg-white/50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 font-semibold mb-1">
                  {t('address.coordinates')}
                </p>
                <p className="text-gray-900 font-mono text-sm">
                  46.1040° N, 25.5865° E
                </p>
              </div>
            </div>

            {/* Directions Button */}
            <div className="mt-8">
              <a
                href="https://maps.google.com/?q=46.1040,25.5865"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full inline-flex items-center justify-center gap-3 px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('address.getDirections')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - How to Find Us */}
      <section className="design-section-gap rounded-3xl overflow-hidden bg-gradient-to-br from-[#faf8f6] to-white">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up design-content-pad lg:p-12 xl:p-16">
          <h2 className="font-poppins text-5xl md:text-6xl font-bold mb-8">
            <span className="text-[#801515]">{t('cta.heading')}</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 mb-12 leading-relaxed">
            {t('cta.description')}
          </p>
          <Link
            href="/how-to-find-us"
            className="group inline-flex items-center gap-3 px-[26px] py-7 bg-[#801515] hover:bg-[#601010] text-white font-serif text-2xl xl:text-3xl rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 transform hover:scale-105"
          >
            {t('cta.button')}
            <ArrowRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="design-section-gap bg-[#faf8f6] rounded-3xl overflow-hidden">
        <div className="max-w-3xl mx-auto design-content-pad lg:py-20">
          <div className="text-center mb-14 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.heading')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <div className="space-y-4">
            {Array.from({ length: faqCount }, (_, idx) => idx + 1).map((n, i) => (
              <div
                key={n}
                className="observe-me slide-up"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 p-6 md:p-8 text-left group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-poppins text-lg md:text-xl font-bold text-gray-900 leading-tight pr-4">
                      {t(`faq.q${n}`)}
                    </h3>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-[#801515]/10 flex items-center justify-center transition-transform duration-300 ${expandedFaq === i ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-5 h-5 text-[#801515]" />
                    </div>
                  </div>

                  {expandedFaq === i && (
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed mt-5 pt-5 border-t border-gray-100">
                      {t(`faq.a${n}`)}
                    </p>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
